<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/ScheduleMessageModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { dateTime } = useLocalizedTime();
const store = useStore();

const isFetching = ref(false);
const showEditModal = ref(false);
const editingMessage = ref(null);

const scheduledMessagesByConversationId = useMapGetter(
  'scheduledMessages/getScheduledMessagesByConversationId'
);

const pendingMessages = computed(() =>
  scheduledMessagesByConversationId
    .value(props.conversationId)
    .filter(message => message.status === 'pending')
    .sort((a, b) => a.scheduled_at - b.scheduled_at)
);

const editingMessageScheduledAt = computed(() =>
  editingMessage.value
    ? new Date(editingMessage.value.scheduled_at * 1000)
    : null
);

const preview = message => message.content || message.template_id || '';

const fetchScheduledMessages = async () => {
  isFetching.value = true;
  try {
    await store.dispatch(
      'scheduledMessages/fetchByConversation',
      props.conversationId
    );
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchScheduledMessages);

const openEditModal = message => {
  editingMessage.value = message;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingMessage.value = null;
};

const onConfirmEdit = async ({ scheduledAt, content, cancelOnReply }) => {
  try {
    await store.dispatch('scheduledMessages/update', {
      conversationId: props.conversationId,
      id: editingMessage.value.id,
      scheduled_at: scheduledAt,
      content,
      cancel_on_reply: cancelOnReply,
    });
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.UPDATE_SUCCESS'));
    closeEditModal();
  } catch (error) {
    useAlert(t('CONVERSATION.SCHEDULE_MESSAGE.UPDATE_ERROR'));
  }
};

const cancelScheduledMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/cancel', {
      conversationId: props.conversationId,
      id: message.id,
    });
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.CANCEL_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-0.5 p-2">
    <div v-if="isFetching" class="flex justify-center p-3">
      <Spinner class="size-5" />
    </div>
    <p
      v-else-if="!pendingMessages.length"
      class="p-3 text-sm text-center text-n-slate-11"
    >
      {{ t('CONVERSATION_SIDEBAR.SCHEDULED_MESSAGES.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-0.5">
      <li
        v-for="message in pendingMessages"
        :key="message.id"
        class="flex items-center gap-2 px-2 py-2 transition-colors rounded-lg hover:bg-n-slate-3 group"
      >
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate text-n-slate-12 mb-0.5">
            {{ preview(message) }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ dateTime(message.scheduled_at) }}
          </p>
        </div>
        <NextButton
          ghost
          slate
          sm
          icon="i-lucide-pencil"
          class="opacity-0 group-hover:opacity-100"
          :aria-label="t('CONVERSATION_SIDEBAR.SCHEDULED_MESSAGES.EDIT')"
          @click="openEditModal(message)"
        />
        <NextButton
          ghost
          ruby
          sm
          icon="i-lucide-trash-2"
          class="opacity-0 group-hover:opacity-100"
          :aria-label="t('SCHEDULED_MESSAGES.CANCEL')"
          @click="cancelScheduledMessage(message)"
        />
      </li>
    </ul>

    <woot-modal v-model:show="showEditModal" :on-close="closeEditModal">
      <ScheduleMessageModal
        v-if="editingMessage"
        is-editing
        :show-message-field="!editingMessage.template_id"
        :initial-scheduled-at="editingMessageScheduledAt"
        :initial-content="editingMessage.content"
        :initial-cancel-on-reply="editingMessage.cancel_on_reply"
        @close="closeEditModal"
        @confirm="onConfirmEdit"
      />
    </woot-modal>
  </div>
</template>
